import { useMemo } from 'react';

import type { DraftPresentation } from '../../contracts/approval-kinds';
import { applySpanEdit } from './apply-span-edit';
import { extractStreamingDraftBody } from './extract-streaming-draft-body';
import type { TextEditPreview } from './text-edit-preview';

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const NOTHING_YET = {
  displayBody: '',
  tintAgainst: undefined,
  removesOnly: false,
  copyText: '',
  copiesReplacement: false,
};

const SPAN_OLD_KEY = 'old_string';
const SPAN_NEW_KEY = 'new_string';

/**
 * What a draft card shows, tints, copies and saves, from the tool input or its
 * stream.
 *
 * The card shows the text as it will be saved. The backend's `serverPreview`
 * is that text, computed by the save itself, and wins. Without it, when the
 * current text is known (`beforeText`), an edit's future text is that text
 * with the edit applied, and the body is tinted against it. When neither is
 * known, a span edit shows only its replacement, tinted against the text it
 * replaces. While streaming, a span edit shows only its replacement so far.
 * Removed text shows only for a deletion, which leaves nothing to tint.
 */
export const useDraftPreview = ({
  toolInput,
  partialInput,
  draft,
  beforeText,
  serverPreview,
  isStreaming = false,
}: {
  toolInput: Record<string, unknown>;
  partialInput?: string;
  draft: DraftPresentation | null;
  beforeText?: string;
  serverPreview?: TextEditPreview | null;
  isStreaming?: boolean;
}) => {
  const inputBody = draft ? readString(toolInput[draft.body_key]) : '';
  const streamedBody = draft
    ? extractStreamingDraftBody(partialInput, draft.body_key)
    : '';
  const spanOldString =
    readString(toolInput[SPAN_OLD_KEY]) ||
    extractStreamingDraftBody(partialInput, SPAN_OLD_KEY);
  const spanNewString =
    readString(toolInput[SPAN_NEW_KEY]) ||
    extractStreamingDraftBody(partialInput, SPAN_NEW_KEY);
  const isSpanEdit = !inputBody && Boolean(spanOldString || spanNewString);
  const replaceAll = toolInput.replace_all === true;

  const future = useMemo((): string | null => {
    if (serverPreview) return null;
    if (inputBody) return inputBody;
    if (!draft || !beforeText || !spanOldString) return null;
    const rewritten = applySpanEdit(beforeText, spanOldString, spanNewString, replaceAll);
    return rewritten.applied ? rewritten.text : null;
  }, [
    beforeText,
    draft,
    inputBody,
    replaceAll,
    serverPreview,
    spanNewString,
    spanOldString,
  ]);

  if (serverPreview) {
    // A deletion leaves nothing to highlight, so the card names what it
    // removes. Only when the server's text is that removal: deleting a whole
    // consult note restores the generated one instead, which the card shows.
    const deletion =
      isSpanEdit && !spanNewString
        ? applySpanEdit(serverPreview.before, spanOldString, '', replaceAll)
        : null;
    if (deletion?.applied && deletion.text === serverPreview.after) {
      return {
        displayBody: spanOldString,
        tintAgainst: undefined,
        removesOnly: true,
        copyText: '',
        copiesReplacement: false,
      };
    }
    return {
      displayBody: serverPreview.after,
      tintAgainst: serverPreview.before,
      removesOnly: false,
      copyText: serverPreview.after,
      copiesReplacement: false,
    };
  }
  // Mid-stream a span edit can't tell an edit from a deletion until its
  // replacement starts, and where it lands is the server's to say, so it streams
  // only the replacement, which the approval then places in the whole text.
  if (isStreaming && !streamedBody) {
    return spanNewString ? { ...NOTHING_YET, displayBody: spanNewString } : NOTHING_YET;
  }
  if (future) {
    return {
      displayBody: future,
      tintAgainst: beforeText,
      removesOnly: false,
      copyText: future,
      copiesReplacement: false,
    };
  }
  if (isSpanEdit) {
    // No current text to apply the edit to: show what it writes, tinted
    // against what it replaces. A deletion writes nothing, so it shows what
    // it removes.
    return {
      displayBody: spanNewString || spanOldString,
      tintAgainst: spanNewString ? spanOldString : undefined,
      removesOnly: !spanNewString,
      copyText: spanNewString,
      copiesReplacement: true,
    };
  }
  return {
    displayBody: streamedBody,
    tintAgainst: undefined,
    removesOnly: false,
    copyText: streamedBody,
    copiesReplacement: false,
  };
};
